// Маршруты войск по суше карты archipelago-v7: только по земле, дорогам и мостам (длинные мосты
// между островами, малые — на пересечениях дорог с реками). Реки, горы и море не пройти.
// По дорогам войска идут быстрее, по лесу — медленнее (и рискуют попасть в засаду).
import { CELL, archipelagoGrid, isWalkableCell } from './archipelago.js';
import { projectOnBridge, tileBridges } from './bridgeGeometry.js';
import type { Point, Terrain } from './types.js';

// ───── Путь войск по суше ─────

/** Стоимость шага по местности (в «клетках равнины»); Infinity — не пройти. */
export const TERRAIN_COST: Record<Terrain, number> = {
  plain: 1,
  coast: 1,
  bridge: 0.9,
  road: 0.8,
  desert: 1.2,
  snow: 1.3,
  forest: 1.8,
  hill: 2.2,
  river: Number.POSITIVE_INFINITY,
  mountain: Number.POSITIVE_INFINITY,
  sea: Number.POSITIVE_INFINITY,
};

export interface LandPath {
  reachable: boolean;
  /** Позиции маршрута; на мостах дробные точки на оси настила. */
  cells: Point[];
  /** Упрощённая ломаная (точки поворота) — для отрисовки и движения на карте. */
  points: Point[];
  /** Стоимость пути (клеток равнины): по ней считается время похода. */
  cost: number;
  /** Сколько клеток леса на пути (засады). */
  forest: number;
  /** Лесные клетки на момент расчёта; у старых сохранённых маршрутов поля нет. */
  forestCells?: Point[];
}

export interface LandDangerCircle extends Point {
  radius: number;
}
/** Снимок актуальной местности; key меняется при изменении расчистки. */
export interface LandPathTerrain {
  key: string;
  cells: Uint8Array;
  rows: number;
}
export interface LandPathOptions {
  terrain?: LandPathTerrain;
  avoidForest?: boolean;
  dangerCircles?: LandDangerCircle[];
  /** Альтернативные клетки прибытия (например, позиция осады вне радиуса башни). */
  destinations?: Point[];
}
export interface MarchRoute extends LandPath {
  /** Версия расчистки, на которой зафиксирован маршрут (сама сетка не сохраняется). */
  terrainKey?: string;
  avoidDanger: boolean;
  safeRoute: boolean;
  /** Доля времени до каждой клетки/точки, зафиксированная по рельефу при отправке. */
  progress?: number[];
  pointProgress?: number[];
}

export function landPathProgress(
  path: Pick<LandPath, 'cells'>,
  _seed: number,
  size: number,
  terrain?: LandPathTerrain,
): number[] {
  const grid = (terrain ?? archipelagoGrid(size)).cells;
  const costs = path.cells.map((p, i) =>
    i === 0 || i === path.cells.length - 1 ? 1 : cellCost(grid[Math.round(p.y) * size + Math.round(p.x)]!),
  );
  const result = [0];
  for (let i = 1; i < path.cells.length; i++) {
    const a = path.cells[i - 1]!;
    const b = path.cells[i]!;
    result.push(result[i - 1]! + (Math.hypot(b.x - a.x, b.y - a.y) * (costs[i - 1]! + costs[i]!)) / 2);
  }
  const total = result.at(-1) ?? 0;
  return result.map((n) => (total > 0 && Number.isFinite(total) ? n / total : 0));
}

export function landPathPointProgress(path: LandPath, progress: number[]): number[] {
  let cursor = 0;
  return path.points.map((point) => {
    while (
      cursor + 1 < path.cells.length &&
      (path.cells[cursor]!.x !== point.x || path.cells[cursor]!.y !== point.y)
    )
      cursor++;
    return progress[cursor] ?? 0;
  });
}

const pathCache = new Map<string, LandPath>();

function segmentNearCircle(a: Point, b: Point, circle: LandDangerCircle): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const k = Math.max(
    0,
    Math.min(1, ((circle.x - a.x) * dx + (circle.y - a.y) * dy) / (dx * dx + dy * dy || 1)),
  );
  return Math.hypot(a.x + dx * k - circle.x, a.y + dy * k - circle.y) <= circle.radius + 1e-9;
}

function clearLandSegment(
  size: number,
  p: Point,
  q: Point,
  options: LandPathOptions,
  allowedStart: Point,
  allowedEnd?: Point,
): boolean {
  // Лес у самого старта и у цели неизбежен (замок или цель стоят в лесу) — он не делает путь опасным
  const nearEnds = (x: number, y: number) =>
    Math.hypot(x - allowedStart.x, y - allowedStart.y) <= FOREST_END_ZONE ||
    (!!allowedEnd && Math.hypot(x - allowedEnd.x, y - allowedEnd.y) <= FOREST_END_ZONE);
  const { cells, rows } = options.terrain ?? archipelagoGrid(size);
  const safeCell = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= size || y >= rows) return false;
    const cell = cells[y * size + x]!;
    return (
      Number.isFinite(cellCost(cell)) &&
      (!options.avoidForest || cell !== CELL.FOREST || nearEnds(x, y)) &&
      !(options.dangerCircles ?? []).some((c) => Math.hypot(x - c.x, y - c.y) <= c.radius + 1e-9)
    );
  };
  if ((options.dangerCircles ?? []).some((circle) => segmentNearCircle(p, q, circle))) return false;
  const steps = Math.max(1, Math.ceil(Math.hypot(q.x - p.x, q.y - p.y) * 8));
  let previous = { x: Math.round(p.x), y: Math.round(p.y) };
  for (let i = 0; i <= steps; i++) {
    const x = Math.round(p.x + ((q.x - p.x) * i) / steps);
    const y = Math.round(p.y + ((q.y - p.y) * i) / steps);
    if (!safeCell(x, y)) return false;
    if (x !== previous.x && y !== previous.y && (!safeCell(x, previous.y) || !safeCell(previous.x, y)))
      return false;
    previous = { x, y };
  }
  return true;
}

/** Проверяет и фактические клетки, и всю нарисованную линию, включая последнюю клетку. */
export function landPathAvoidsDanger(path: LandPath, size: number, options: LandPathOptions): boolean {
  if (!path.reachable || !path.cells.length || !path.points.length) return false;
  const start = path.cells[0]!;
  const end = path.cells[path.cells.length - 1]!;
  return [path.cells, path.points].every((points) =>
    points.every((p, i) => clearLandSegment(size, i ? points[i - 1]! : p, p, options, start, end)),
  );
}

interface PathQueueEntry {
  index: number;
  score: number;
}

// При улучшении пути старая запись остаётся в очереди со своим прежним приоритетом.
// Изменяемый f[index] нельзя использовать для сравнения уже лежащих записей:
// это нарушает порядок heap и может вернуть неоптимальный маршрут.
function heapPush(heap: PathQueueEntry[], entry: PathQueueEntry) {
  heap.push(entry);
  let c = heap.length - 1;
  while (c > 0) {
    const p = (c - 1) >> 1;
    if (heap[p]!.score <= heap[c]!.score) break;
    [heap[p], heap[c]] = [heap[c]!, heap[p]!];
    c = p;
  }
}
function heapPop(heap: PathQueueEntry[]): PathQueueEntry {
  const top = heap[0]!;
  const last = heap.pop()!;
  if (heap.length) {
    heap[0] = last;
    let c = 0;
    for (;;) {
      const l = c * 2 + 1;
      const r = l + 1;
      let m = c;
      if (l < heap.length && heap[l]!.score < heap[m]!.score) m = l;
      if (r < heap.length && heap[r]!.score < heap[m]!.score) m = r;
      if (m === c) break;
      [heap[m], heap[c]] = [heap[c]!, heap[m]!];
      c = m;
    }
  }
  return top;
}

/** «Обходить леса»: доплата за лесную клетку (лес почти всегда обходится, как бы далеко ни было). */
export const FOREST_AVOID_COST = 40;
/** Лес в пределах этого радиуса от старта и цели не делает путь опасным (замок или цель стоят в лесу). */
const FOREST_END_ZONE = 2.5;

/** Стоимость шага по клетке (CELL); Infinity — не пройти. */
const CELL_COST: number[] = [];
CELL_COST[CELL.LAND] = TERRAIN_COST.plain;
CELL_COST[CELL.COAST] = TERRAIN_COST.coast;
CELL_COST[CELL.FOREST] = TERRAIN_COST.forest;
CELL_COST[CELL.ROAD] = TERRAIN_COST.road;
CELL_COST[CELL.PLAZA] = TERRAIN_COST.road;
CELL_COST[CELL.BRIDGE] = TERRAIN_COST.bridge;
CELL_COST[CELL.CROSSING] = TERRAIN_COST.bridge;
CELL_COST[CELL.HILL] = TERRAIN_COST.hill;

function cellCost(c: number): number {
  return isWalkableCell(c) ? (CELL_COST[c] ?? 1) : Number.POSITIVE_INFINITY;
}

function astar(size: number, from: Point, to: Point, options: LandPathOptions): number[] | null {
  const { cells, rows } = options.terrain ?? archipelagoGrid(size);
  const fx = Math.round(from.x);
  const fy = Math.round(from.y);
  const tx = Math.round(to.x);
  const ty = Math.round(to.y);
  if (fx < 0 || fy < 0 || tx < 0 || ty < 0 || fx >= size || tx >= size || fy >= rows || ty >= rows)
    return null;
  const n = size * rows;
  const start = fy * size + fx;
  const dangers = options.dangerCircles ?? [];
  const blocked = new Uint8Array(n);
  // Обход лесов — «мягкий»: лесная клетка во много раз дороже, путь идёт вокруг леса, даже если крюк через
  // всю карту, и заходит в лес только там, где иначе не пройти (цель или замок в лесу)
  const forestExtra = options.avoidForest ? FOREST_AVOID_COST : 0;
  // Замок может стоять в лесу: разрешаем выйти со стартовой клетки, остальные леса запрещены.
  blocked[start] = 0;
  const nearby = new Map<number, LandDangerCircle[]>();
  for (const circle of dangers) {
    for (
      let y = Math.max(0, Math.floor(circle.y - circle.radius - 2));
      y <= Math.min(rows - 1, Math.ceil(circle.y + circle.radius + 2));
      y++
    ) {
      for (
        let x = Math.max(0, Math.floor(circle.x - circle.radius - 2));
        x <= Math.min(size - 1, Math.ceil(circle.x + circle.radius + 2));
        x++
      ) {
        const i = y * size + x;
        const list = nearby.get(i) ?? [];
        list.push(circle);
        nearby.set(i, list);
        if (Math.hypot(x - circle.x, y - circle.y) <= circle.radius + 1e-9) blocked[i] = 1;
      }
    }
  }
  const targets = (options.destinations ?? [to])
    .map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) }))
    .filter((p) => p.x >= 0 && p.y >= 0 && p.x < size && p.y < rows && !blocked[p.y * size + p.x]);
  if (!targets.length || blocked[start]) return null;
  const goals = new Set(targets.map((p) => p.y * size + p.x));
  // Обычные цели допускают свой замок на клетке; запрет опасности никогда не отменяется.
  const cost = (i: number) =>
    blocked[i]
      ? Number.POSITIVE_INFINITY
      : i === start || goals.has(i)
        ? 1
        : cellCost(cells[i]!) + (cells[i] === CELL.FOREST ? forestExtra : 0);
  const g = new Float64Array(n).fill(Number.POSITIVE_INFINITY);
  const f = new Float64Array(n);
  const prev = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);
  const minX = Math.min(...targets.map((p) => p.x));
  const maxX = Math.max(...targets.map((p) => p.x));
  const minY = Math.min(...targets.map((p) => p.y));
  const maxY = Math.max(...targets.map((p) => p.y));
  // Расстояние до bounding box множества целей — допустимая оценка для A*.
  const h = (x: number, y: number) => {
    const dx = Math.max(minX - x, 0, x - maxX);
    const dy = Math.max(minY - y, 0, y - maxY);
    return (Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy)) * TERRAIN_COST.road;
  };
  let goal = -1;
  const heap: PathQueueEntry[] = [];
  g[start] = 0;
  f[start] = h(fx, fy);
  heapPush(heap, { index: start, score: f[start]! });
  while (heap.length) {
    const entry = heapPop(heap);
    const cur = entry.index;
    if (closed[cur] || entry.score !== f[cur]) continue;
    if (goals.has(cur)) {
      goal = cur;
      break;
    }
    closed[cur] = 1;
    const cx = cur % size;
    const cy = (cur - cx) / size;
    const cc = cost(cur);
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = cx + dx;
        const ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= size || ny >= rows) continue;
        const j = ny * size + nx;
        if (closed[j]) continue;
        const nc = cost(j);
        if (!Number.isFinite(nc)) continue;
        // По диагонали — только если обе соседние клетки проходимы (реку и берег не «срезать»)
        if (dx && dy && (!Number.isFinite(cost(cy * size + nx)) || !Number.isFinite(cost(ny * size + cx))))
          continue;
        if (
          (nearby.get(cur) ?? []).some((circle) =>
            segmentNearCircle({ x: cx, y: cy }, { x: nx, y: ny }, circle),
          )
        )
          continue;
        const ng = g[cur]! + ((cc + nc) / 2) * (dx && dy ? Math.SQRT2 : 1);
        if (ng < g[j]!) {
          g[j] = ng;
          prev[j] = cur;
          f[j] = ng + h(nx, ny);
          heapPush(heap, { index: j, score: f[j]! });
        }
      }
  }
  if (goal < 0 || (prev[goal] === -1 && goal !== start)) return null;
  const out: number[] = [];
  for (let i = goal; i !== -1; i = prev[i]!) out.push(i);
  return out.reverse();
}

/**
 * Путь войска по суше между клетками: в обход рек, гор и моря, через мосты. Старт и цель проходимы
 * всегда. Если пути нет вовсе (цель в море) — по прямой, втрое медленнее.
 * seed оставлен в сигнатуре для совместимости: местность одна на все миры.
 */
function alignBridgePath(list: Point[], size: number, options: LandPathOptions): Point[] {
  const grid = (options.terrain ?? archipelagoGrid(size)).cells;
  const bridges = tileBridges(size);
  const labels = list.map((p) => {
    const cell = grid[Math.round(p.y) * size + Math.round(p.x)];
    const kind = cell === CELL.BRIDGE ? 'sea' : cell === CELL.CROSSING ? 'river' : null;
    if (!kind) return null;
    return (
      bridges.find(
        (b) =>
          b.kind === kind &&
          Math.hypot(p.x - projectOnBridge(p, b).x, p.y - projectOnBridge(p, b).y) <=
            Math.max(1.1, b.halfWidth) + 1e-8,
      ) ?? null
    );
  });
  const result = list.slice();
  for (let i = 0; i < list.length; i++) {
    const bridge = labels[i];
    if (!bridge) continue;
    let end = i;
    while (end + 1 < list.length && labels[end + 1]?.id === bridge.id) end++;
    const projections = list.slice(i, end + 1).map((p) => projectOnBridge(p, bridge));
    let positive = false,
      negative = false;
    for (let k = 1; k < projections.length; k++) {
      const delta = projections[k]!.fraction - projections[k - 1]!.fraction;
      positive ||= delta > 1e-8;
      negative ||= delta < -1e-8;
    }
    const replacement = projections.map((p, k) =>
      i + k === 0 || i + k === list.length - 1 ? list[i + k]! : { x: p.x, y: p.y },
    );
    const check = [
      ...(i > 0 ? [result[i - 1]!] : []),
      ...replacement,
      ...(end + 1 < list.length ? [list[end + 1]!] : []),
    ];
    // Безопасный обход башни имеет приоритет: ось допускается только при свободном проходе.
    if (
      !(positive && negative) &&
      check.every((p, k) => clearLandSegment(size, k ? check[k - 1]! : p, p, options, list[0]!))
    ) {
      for (let k = 0; k < replacement.length; k++) result[i + k] = replacement[k]!;
    }
    i = end;
  }
  return result.filter(
    (p, i) => i === 0 || Math.hypot(p.x - result[i - 1]!.x, p.y - result[i - 1]!.y) > 1e-9,
  );
}

export function landPath(
  _seed: number,
  size: number,
  from: Point,
  to: Point,
  options: LandPathOptions = {},
): LandPath {
  const { terrain, ...constraints } = options;
  const key = `${size}:${Math.round(from.x)},${Math.round(from.y)}>${Math.round(to.x)},${Math.round(to.y)}:${terrain?.key ?? 'base'}:${JSON.stringify(constraints)}`;
  const hit = pathCache.get(key);
  if (hit) return hit;
  const { cells } = terrain ?? archipelagoGrid(size);
  const idx = astar(size, from, to, options);
  let result: LandPath;
  if (!idx) {
    const d = Math.hypot(to.x - from.x, to.y - from.y);
    result = {
      reachable: false,
      cells: [from, to],
      points: [from, to],
      cost: d * 3,
      forest: 0,
      forestCells: [],
    };
  } else {
    const list = alignBridgePath(
      idx.map((i) => ({ x: i % size, y: Math.floor(i / size) })),
      size,
      options,
    );
    let cost = 0;
    let forest = 0;
    const forestCells: Point[] = [];
    const edgeCosts: number[] = [];
    for (let k = 1; k < list.length; k++) {
      const a = list[k - 1]!;
      const b = list[k]!;
      const ca = k - 1 === 0 ? 1 : cellCost(cells[Math.round(a.y) * size + Math.round(a.x)]!);
      const cell = cells[Math.round(b.y) * size + Math.round(b.x)];
      const cb = k === list.length - 1 ? 1 : cellCost(cell!);
      const stepCost = ((ca + cb) / 2) * Math.hypot(b.x - a.x, b.y - a.y);
      edgeCosts.push((ca + cb) / 2);
      cost += stepCost;
      if (cell === CELL.FOREST && k < list.length - 1) {
        forest++;
        forestCells.push({ x: Math.round(b.x), y: Math.round(b.y) });
      }
    }
    // Убираем только коллинеарные клетки с одинаковой скоростью движения.
    // Срезание поворотов через проходимую
    // местность могло рисовать войско в лесу, хотя cells шли по дороге вокруг
    // него: время, засады и обстрел тогда не совпадали с движением на карте.
    const points: Point[] = [list[0]!];
    for (let k = 1; k < list.length - 1; k++) {
      const before = list[k - 1]!;
      const current = list[k]!;
      const after = list[k + 1]!;
      const dx = current.x - before.x,
        dy = current.y - before.y;
      const ex = after.x - current.x,
        ey = after.y - current.y;
      if (
        Math.abs(dx * ey - dy * ex) > 1e-8 * Math.hypot(dx, dy) * Math.hypot(ex, ey) ||
        dx * ex + dy * ey <= 0 ||
        Math.abs(edgeCosts[k - 1]! - edgeCosts[k]!) > 1e-9
      )
        points.push(current);
    }
    if (list.length > 1) points.push(list[list.length - 1]!);
    result = { reachable: true, cells: list, points, cost: Math.max(cost, 0.5), forest, forestCells };
  }
  pathCache.set(key, result);
  if (pathCache.size > 600) pathCache.delete(pathCache.keys().next().value!);
  return result;
}

/** Точка на пути по доле k (0…1) и направление движения — для войск на карте. */
export function landPathPoint(path: Point[], k: number): Point & { dx: number; dy: number } {
  if (path.length < 2) return { ...(path[0] ?? { x: 0, y: 0 }), dx: 1, dy: 0 };
  let total = 0;
  for (let i = 1; i < path.length; i++)
    total += Math.hypot(path[i]!.x - path[i - 1]!.x, path[i]!.y - path[i - 1]!.y);
  let d = Math.max(0, Math.min(1, k)) * total;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]!;
    const b = path[i]!;
    const seg = Math.hypot(b.x - a.x, b.y - a.y);
    if (d <= seg || i === path.length - 1) {
      const f = seg > 0 ? Math.min(1, d / seg) : 0;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, dx: b.x - a.x, dy: b.y - a.y };
    }
    d -= seg;
  }
  const last = path[path.length - 1]!;
  return { ...last, dx: 1, dy: 0 };
}

// ───── Засады в лесах ─────

export const AMBUSH = {
  /** % шанса за каждую клетку леса на пути (не больше maxPct). */
  perForestPct: 4,
  maxPct: 55,
  /** Меньше этого числа клеток леса — засад нет. */
  minForest: 2,
  /** Сила разбойников — от и до % силы отряда. */
  powerMinPct: 10,
  powerMaxPct: 22,
} as const;

/** Шанс засады (0…1) по числу клеток леса на пути. */
export function ambushChance(forestCells: number): number {
  if (forestCells < AMBUSH.minForest) return 0;
  return Math.min(AMBUSH.maxPct, forestCells * AMBUSH.perForestPct) / 100;
}
